import { useEffect, useRef, useState } from 'react';
import { MIC_TYPES, MIC_TYPE_SHORT, type MicType } from '@micplot/shared';

interface Props {
  label: string;
  /** Show a transmitter-type picker alongside the count. */
  withType?: boolean;
  defaultCount?: number;
  disabled?: boolean;
  onAdd: (count: number, micType: MicType) => void;
}

/**
 * Inline "add N" control.
 *
 * Deliberately not window.prompt(): iOS blocks prompt() inside an installed
 * PWA, which is exactly how this gets used backstage, so the button would
 * appear to do nothing at all.
 */
export default function AddControl({
  label, withType = false, defaultCount = 4, disabled, onAdd,
}: Props) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(String(defaultCount));
  const [micType, setMicType] = useState<MicType>('lav');
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.select();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function submit() {
    const n = Math.floor(Number(count));
    if (!Number.isFinite(n) || n < 1) return;
    onAdd(Math.min(n, 200), micType);
    setOpen(false);
    setCount(String(defaultCount));
  }

  if (!open) {
    return (
      <button className="btn sm" disabled={disabled} onClick={() => setOpen(true)}>
        + Add
      </button>
    );
  }

  return (
    <div className="add-pop" ref={wrapRef}>
      <label className="add-pop-label">{label}</label>
      <div className="row">
        <input
          ref={inputRef} type="number" min={1} max={200} value={count}
          aria-label={label} style={{ width: 68 }}
          onChange={e => setCount(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') submit();
          }}
        />
        {withType && (
          <select
            value={micType} aria-label="Transmitter type" style={{ width: 84 }}
            onChange={e => setMicType(e.target.value as MicType)}
          >
            {MIC_TYPES.map(t => (
              <option key={t} value={t}>{MIC_TYPE_SHORT[t]}</option>
            ))}
          </select>
        )}
        <button className="btn sm primary" onClick={submit}>Add</button>
        <button className="btn sm" onClick={() => setOpen(false)} aria-label="Cancel">✕</button>
      </div>
    </div>
  );
}
